// SuggestionList.tsx
import {
  alpha,
  Avatar,
  Box,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

export type SuggestionListRef = {
  onKeyDown: (props: any) => boolean
}

type Item = {
  id: string
  name: string
  label: string
  imageUrl?: string
  // email?: string // add if you’d like to show a secondary line
}

type Props = {
  // Provided by @tiptap/suggestion
  items: Item[]
  command: (item: Item) => void
  editor: any
  query: string
}

const SuggestionList = forwardRef<SuggestionListRef, Props>((props, ref) => {
  const theme = useTheme()
  const { items, command, query } = props
  const [selectedIndex, setSelectedIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)

  // Use refs to store current values for use in useImperativeHandle
  const itemsRef = useRef(items)
  const commandRef = useRef(command)
  const selectedIndexRef = useRef(selectedIndex)

  // Keep refs up to date
  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    commandRef.current = command
  }, [command])

  useEffect(() => {
    selectedIndexRef.current = selectedIndex
  }, [selectedIndex])

  useEffect(() => {
    setSelectedIndex(0)
  }, [items, query])

  const selectItem = (idx: number) => {
    const currentItems = itemsRef.current
    const currentCommand = commandRef.current
    const item = currentItems[idx]
    if (!item || !currentCommand) return
    // Map to Mention node attrs the extension expects:
    // Pass the item with label set to name for the mention extension
    currentCommand({
      id: item.id,
      name: item.name,
      label: item.name,
      imageUrl: item.imageUrl,
    } as Item & { label: string })
  }

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      const currentItems = itemsRef.current
      if (!currentItems?.length) return false
      if (event.key === 'ArrowDown') {
        setSelectedIndex(i => (i + 1) % currentItems.length)
        return true
      }
      if (event.key === 'ArrowUp') {
        setSelectedIndex(i => (i - 1 + currentItems.length) % currentItems.length)
        return true
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        selectItem(selectedIndexRef.current)
        return true
      }
      if (event.key === 'Escape') {
        return true // let renderer close it
      }
      return false
    },
  }))

  const renderHighlighted = (name: string) => {
    const q = (query || '').trim()
    if (!q) return name
    const idx = name.toLowerCase().indexOf(q.toLowerCase())
    if (idx < 0) return name
    const before = name.slice(0, idx)
    const match = name.slice(idx, idx + q.length)
    const after = name.slice(idx + q.length)
    return (
      <>
        {before}
        <Typography component="span" fontWeight={700}>
          {match}
        </Typography>
        {after}
      </>
    )
  }

  if (!items?.length) {
    return (
      <Paper
        elevation={6}
        ref={rootRef}
        sx={{
          maxHeight: 280,
          width: 320,
          overflow: 'auto',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.25,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No matches
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper
      elevation={8}
      ref={rootRef}
      role="listbox"
      aria-label="User mentions"
      sx={{
        maxHeight: 320,
        width: 320,
        overflowY: 'auto',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        boxShadow: theme.shadows[8],
      }}
    >
      <List dense disablePadding>
        {items.map((u, i) => {
          const selected = i === selectedIndex
          return (
            <Box key={u.id}>
              <ListItemButton
                role="option"
                selected={selected}
                onMouseEnter={() => setSelectedIndex(i)}
                onMouseDown={e => {
                  // keep editor focus; TipTap suggests preventDefault here
                  e.preventDefault()
                  selectItem(i)
                }}
                sx={{
                  py: 0.75,
                  px: 1,
                  '&.Mui-selected': {
                    bgcolor: theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.main',
                    color: theme.palette.mode === 'dark' ? 'primary.contrastText' : 'primary.contrastText',
                    '& .MuiListItemText-secondary': {
                      color: 'inherit',
                      opacity: 0.9,
                    },
                    '& .MuiAvatar-root': {
                      border: `2px solid ${alpha(theme.palette.text.primary, 0.7)}`,
                    },
                  },
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'action.hover',
                  },
                }}
              >
                <ListItemAvatar>
                  {u.imageUrl ? (
                    <Avatar src={u.imageUrl} alt={u.name} />
                  ) : (
                    <Avatar>{u.name?.charAt(0)?.toUpperCase()}</Avatar>
                  )}
                </ListItemAvatar>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body2' }}
                  // secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  primary={renderHighlighted(u.name)}
                  // secondary={u.email}
                />
              </ListItemButton>
              {i < items.length - 1 && <Divider component="li" sx={{ mx: 1 }} />}
            </Box>
          )
        })}
      </List>
    </Paper>
  )
})

export default SuggestionList
